import { isBoolean } from 'lodash-es'
import { computed, onMounted, onUnmounted, ref, type Ref } from '@/composition/vue/compositionApi'
import { getDateTimeService, getUserService } from '@/core/container/ioc'
import type { DateFormatesType } from '@/services/types'
import { DATE_FORMATES } from '@/services/types'
import type Timezone from '@/data/models/domain/Timezone'
import type { Nullable } from '@/types'
import type { DateAlike } from '@/services/dateTimeService'

export const useCurrentDate = (format: DateFormatesType, tick = 10000) => {
  const date = ref()
  let timer: ReturnType<typeof setInterval> | undefined

  const updateDate = () => {
    const dateTimeService = getDateTimeService()
    date.value = dateTimeService.format(dateTimeService.now(), format)
  }

  onMounted(() => {
    timer = setInterval(updateDate, tick)
  })

  onUnmounted(() => {
    clearInterval(timer)
  })

  updateDate()

  return date
}

export const hardChangeDateForTimezone = (date: Date | string | number, timezone?: Timezone) =>
  getDateTimeService().hardChangeDateForTimezone(date, timezone)

/**
 * Formats any date in the user's timezone
 * @param date - Date | ISO string
 * @param format - https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @param formatRelative - boolean
 * @param noTime - boolean
 */
export const formatDateForUserTimezone = (
  date?: DateAlike,
  format: DateFormatesType = DATE_FORMATES.GENERAL_FORMAT,
  formatRelative = true,
  noTime = false,
) => {
  const timezone = useCurrentUserTimezone()

  return formatDateForTimezone(date, timezone, format, formatRelative, noTime)
}

export const formatDateForTimezone = (
  date?: DateAlike,
  timezone?: Timezone,
  format: DateFormatesType = DATE_FORMATES.GENERAL_FORMAT,
  formatRelative = true,
  noTime = false,
) => {
  const currentUser = getUserService().currentUser()

  const force24format = currentUser.displayTimeFormat === '24h'
  const normalizedDate = getDateTimeService().toDate(date)

  return getDateTimeService().formatDateForTimezone(
    normalizedDate,
    format,
    timezone,
    formatRelative,
    force24format,
    noTime,
  )
}

/*
  - if provide method with boolean value - returns it
  - if provide method with non-boolean value - it gets format from current user
 */
export const useIs24hDefaultUserFormat = (is24hProp: Ref<boolean | undefined>) => {
  const { timeFormat } = useUserTimeFormat()

  const is24hComputed = computed(() => (isBoolean(is24hProp.value) ? is24hProp.value : timeFormat.value === '24h'))

  return {
    is24hComputed,
  }
}

export const useCurrentUserTimezone = () => {
  const currentUser = getUserService().currentUser(['timezone'])
  const { timezone } = currentUser

  return timezone
}

export const useUserTimeFormat = () => {
  const userService = getUserService()
  const currentUser = userService.currentUser()

  const timeFormat = computed(() => currentUser.displayTimeFormat)
  const is24h = computed(() => timeFormat.value === '24h')

  return {
    timeFormat,
    is24h,
  }
}

export const startOfDay = (date: Date | string | null) => {
  return userTimezoneToUtc(getDateTimeService().startOfDay(utcToUserTimezone(date)))
}

export const endOfDay = (date: Date | string | null) => {
  return userTimezoneToUtc(getDateTimeService().endOfDay(utcToUserTimezone(date)))
}

export const isToday = (date: Date) => {
  const timezone = useCurrentUserTimezone()

  return getDateTimeService().isToday(date, timezone?.timezone)
}

export const isTomorrow = (date: Date) => {
  const timezone = useCurrentUserTimezone()

  return getDateTimeService().isTomorrow(date, timezone?.timezone)
}

export const isYesterday = (date: Date) => {
  const timezone = useCurrentUserTimezone()

  return getDateTimeService().isYesterday(date, timezone?.timezone)
}

export const isCurrentMonth = (date: Date) => {
  const dateTimeService = getDateTimeService()

  const timezone = useCurrentUserTimezone()

  return dateTimeService.isCurrentMonth(date, timezone?.timezone)
}

export const isCurrentWeek = (date: Date) => {
  const dateTimeService = getDateTimeService()

  const timezone = useCurrentUserTimezone()

  return dateTimeService.isCurrentWeek(date, timezone?.timezone)
}

export const isCurrentYear = (date: Date) => {
  const dateTimeService = getDateTimeService()

  const timezone = useCurrentUserTimezone()

  return dateTimeService.isCurrentYear(date, timezone?.timezone)
}

export const isPast = (dateStr: string) => {
  return getDateTimeService().isBefore(new Date(dateStr), new Date())
}

export const isValidDate = (date: Date) => {
  return getDateTimeService().isValid(date)
}

export const isInFormat = (dateStr: string, format: DateFormatesType) => {
  return getDateTimeService().isInFormat(dateStr, format)
}

export const getDateFromStringFormat = (dateStr: string, format: DateFormatesType) => {
  return getDateTimeService().getDateFromStringFormat(dateStr, format)
}

export const userTimezoneToUtc = (dateStr: string | Date) => {
  const timezone = useCurrentUserTimezone()
  return getDateTimeService().zonedTimeToUtc(dateStr, timezone.timezone)
}

export const utcToUserTimezone = (date?: string | Date | null) => {
  const timezone = useCurrentUserTimezone()

  return getDateTimeService().utcToZonedTime(
    date ? new Date(date).toISOString() : new Date().toISOString(),
    timezone.timezone,
  )
}

export const toUserTimezoneIsoString = (date: Date) =>
  formatDateForTimezone(userTimezoneToUtc(date), undefined, DATE_FORMATES.ISO_FORMAT_WITH_TIMEZONE, false)

// Date-only values are stored as UTC midnight, so the calendar day reads the same in every timezone
export const toUtcDateOnlyIsoString = (userTimezoneIsoString: string): string => {
  if (!userTimezoneIsoString) {
    return ''
  }

  const day = getDateTimeService().format(
    utcToUserTimezone(userTimezoneIsoString),
    DATE_FORMATES.DATE_ONLY_FORMAT,
    false,
    false,
  )

  return `${day}T00:00:00+00:00`
}

export const fromUtcDateOnlyIsoString = (utcIsoString: string): string => {
  if (!utcIsoString) {
    return ''
  }

  const dateTimeService = getDateTimeService()
  const day = dateTimeService.format(
    dateTimeService.toDate(utcIsoString),
    DATE_FORMATES.DATE_ONLY_FORMAT,
    false,
    false,
    '+00',
  )

  return toUserTimezoneIsoString(dateTimeService.getDateFromStringFormat(day, DATE_FORMATES.DATE_ONLY_FORMAT))
}

export const useDateInTime = (date: Ref<Nullable<string>>) => {
  const isDateInPast = computed(() => (date.value ? isPast(date.value) : false))
  const isDateComingSoon = computed(() =>
    date.value ? !isPast(date.value) && (isToday(new Date(date.value)) || isTomorrow(new Date(date.value))) : false,
  )

  return {
    isDateInPast,
    isDateComingSoon,
  }
}
